import React from "react";
import styled from "styled-components";
import { UI_CAPTION, UI_TAG, colors2, TooltipV2 } from "oolib";
import { DropdownSingle } from "oolib/dist/v2/components/Dropdowns/DropdownSingle";
import { CaretDown, Info } from "@phosphor-icons/react";
import { DatesThatMatterPanel } from "@/components/analysisModules/TopicClusteringTreeView";

/**
 * The period controls. `PeriodSelector` is the whole thing — "Period (i)
 * [x] vs [y]": the period, and the period its changes are measured against,
 * both read from and written to the shared period selection
 * (usePeriodSelection). One control for every period-scoped surface — the
 * Topics and Priorities sidebars (stacked: label above the row) and the
 * Overview (`inline`: all on one row). The (i) answers "filtered on WHICH
 * date?" (the date that matters, per dataset).
 *
 * `PeriodPicker` is one dropdown of it: a period option out of a list.
 *   period        the selected option ({ value, display, … })
 *   options       the period options to offer
 *   onChange(value)
 *   id            the dropdown's id (one per surface)
 */
// The compact select face the sidebars' dropdowns share.
export const ToolbarSelectTrigger = styled.button`
  background: none;
  border: 1px solid ${colors2.grey20};
  border-radius: 4px;
  padding: 2.5px 5px;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;
  &:hover {
    background: ${colors2.grey5};
  }
`;
const Wrap = styled.div`
  display: flex;
  flex-direction: ${({ $inline }) => ($inline ? "row" : "column")};
  align-items: ${({ $inline }) => ($inline ? "center" : "flex-start")};
  gap: ${({ $inline }) => ($inline ? "0.8rem" : "0.4rem")};
`;
const Row = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.8rem;
`;
// The dropdown sizes its options popup to its own root, which here is only
// as wide as its trigger — so the popup gets a minimum width of its own
// (POPUP_MIN_WIDTH) rather than a squeezed one.
const DropdownSlot = styled.div``;
const POPUP_MIN_WIDTH = "20rem";
const Label = styled(UI_CAPTION)`
  color: ${colors2.grey60};
  display: flex;
  align-items: center;
  gap: 0.3rem;
`;

export const PeriodPicker = ({ period, options, onChange, id }) => {
  if (!options?.length || !period) return null;
  return (
    <DropdownSingle
      id={id}
      value={period.value}
      options={options}
      onChange={(_k, v) => onChange(v?.value ?? v)}
      saveValueAsString
      popOutOfOverflowHiddenParent
      lightboxStyle={{ minWidth: POPUP_MIN_WIDTH }}
      genCustomSelectComp={() => (
        <ToolbarSelectTrigger>
          <UI_TAG style={{ color: colors2.grey80 }}>{period.display}</UI_TAG>
          <CaretDown size={10} weight="bold" color={colors2.grey60} />
        </ToolbarSelectTrigger>
      )}
    />
  );
};

/**
 * "Period (i) [x] vs [y]" over the shared selection.
 *   selection          usePeriodSelection()'s return
 *   id                 prefix for the two dropdowns' ids (one per surface)
 *   inline             everything on one row; else the label above the row
 *   compareLockedNote  given, the "vs" period shows but can't be picked, and
 *                      hovering it says why (Priorities in history mode)
 */
export const PeriodSelector = ({ selection, id, inline = false, compareLockedNote = null }) => {
  const { period, comparePeriod, compareOptions, periodOptions, dateFieldMap, setPeriod, setCompare } = selection;
  if (!periodOptions?.length || !period) return null;
  return (
    <Wrap $inline={inline}>
      <Label>
        Period
        <TooltipV2
          placement="bottom-start"
          showDelay={150}
          showPointer
          primaryContent={{ layout: "custom", render: <DatesThatMatterPanel dateFieldMap={dateFieldMap} /> }}
        >
          <Info size={13} color={colors2.grey40} style={{ display: "block", cursor: "pointer" }} />
        </TooltipV2>
      </Label>
      <Row>
        <DropdownSlot>
          <PeriodPicker id={`${id}Period`} period={period} options={periodOptions} onChange={setPeriod} />
        </DropdownSlot>
        <UI_CAPTION style={{ color: colors2.grey60 }}>vs</UI_CAPTION>
        {compareOptions.length > 0 && comparePeriod && compareLockedNote ? (
          <TooltipV2 text={compareLockedNote} placement="bottom-start" showDelay={150} showPointer wrapperDisplay="inline-block">
            <ToolbarSelectTrigger as="div" style={{ cursor: "not-allowed", opacity: 0.55 }} aria-disabled="true">
              <UI_TAG style={{ color: colors2.grey80 }}>{comparePeriod.display}</UI_TAG>
              <CaretDown size={10} weight="bold" color={colors2.grey60} />
            </ToolbarSelectTrigger>
          </TooltipV2>
        ) : compareOptions.length > 0 && comparePeriod ? (
          <DropdownSlot>
            <PeriodPicker id={`${id}Compare`} period={comparePeriod} options={compareOptions} onChange={setCompare} />
          </DropdownSlot>
        ) : (
          // Nothing of the period's kind lies before it (the first month, or
          // a period with no like-for-like neighbours) — nothing to pick.
          <ToolbarSelectTrigger as="div" style={{ cursor: "default" }} aria-disabled="true">
            <UI_TAG style={{ color: colors2.grey60 }}>no earlier period</UI_TAG>
          </ToolbarSelectTrigger>
        )}
      </Row>
    </Wrap>
  );
};
